import { useCallback, useEffect, memo, useState } from "react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";

import { ButtonV1, DateTimePicker, DropdownV1 } from "components";
import { ErrorMsgV1 } from "CustomStyledComponents";

import pickUpLocationSchema from "../TestSchema";
import { getAddress } from "../makeData";
import { useFetchData } from "hooks";
import { pascalCase } from "utils";

import { JOMS_API_BASE_V1_URL } from "constants";

const FGPickUpDetailsModal = ({ order, onCloseModal, onSavePickUpDetails }) => {
  const [selectedAddress, setSelectedAddress] = useState(
    order?.sellerAddressMetadata,
  );

  const {
    control,
    getValues,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onTouch",
    resolver: yupResolver(pickUpLocationSchema),
  });

  const { data: sellerAddressList, refetch: fetchSellerAddress } = useFetchData(
    "seller-pickup-address",
    `${JOMS_API_BASE_V1_URL}/v1/inventory-data/fg-seller-and-pickup-details/dropdown/${order?.preDoId}`,
  );

  useEffect(() => {
    fetchSellerAddress();
    setValue("deliveryType", order?.deliveryType);
  }, [fetchSellerAddress, order?.deliveryType, setValue]);

  const handleConfirmModal = useCallback(async () => {
    const payload = {
      pickupSlotFrom: getValues("pickupSlotFrom"),
      pickupSlotTo: getValues("pickupSlotTo"),
      sellerAddressMetadata: selectedAddress,
      predoId: order?.preDoId,
    };
    onSavePickUpDetails(payload);
  }, [getValues, onSavePickUpDetails, order?.preDoId, selectedAddress]);

  const handleChangeAddress = useCallback(
    (e) => {
      const selectedAddress = sellerAddressList?.data?.sellerAddresses?.find(
        (item) => item?.vendorLocationDisplayName === e?.target.value,
      );
      setSelectedAddress(selectedAddress);
    },
    [sellerAddressList?.data?.sellerAddresses],
  );

  return (
    <Box as="form" onSubmit={handleSubmit(handleConfirmModal)}>
      <Typography variant="h4">Pick Up Details</Typography>
      <Divider />
      <Grid container py={4}>
        <Grid xs={2.5}>
          <Typography fontWeight={600} mb={1}>
            Pick-up slot: From
          </Typography>
          <Controller
            control={control}
            defaultValue={order?.pickupSlotFrom}
            name={`pickupSlotFrom`}
            render={({ field: { value, onChange } }) => (
              <DateTimePicker
                dateStyle={{ width: "90%" }}
                value={value}
                onChange={onChange}
                disablePast={true}
              />
            )}
          />
          <ErrorMsgV1 msg={errors?.pickupSlotFrom?.message} />
        </Grid>
        <Grid xs={2.5}>
          <Typography fontWeight={600} mb={1}>
            Pick-up slot: To
          </Typography>
          <Controller
            control={control}
            defaultValue={order?.pickupSlotTo}
            name={`pickupSlotTo`}
            render={({ field: { value, onChange } }) => (
              <DateTimePicker
                dateStyle={{ width: "90%" }}
                value={value}
                onChange={onChange}
              />
            )}
          />
          <ErrorMsgV1 msg={errors?.pickupSlotTo?.message} />
        </Grid>
        <Grid xs={2}>
          <Typography fontWeight={600} mb={1}>
            Seller name
          </Typography>
          <Typography>{pascalCase(order?.sellerName)}</Typography>
        </Grid>
        <Grid xs={2.5}>
          <Typography fontWeight={600} mb={1}>
            Pickup location
          </Typography>
          <Controller
            control={control}
            name={"pickUpLocation"}
            defaultValue={
              order?.sellerAddressMetadata?.vendorLocationDisplayName
            }
            render={({ field: { value, onChange } }) => (
              <DropdownV1
                sx={{
                  width: "80%",
                }}
                itemList={sellerAddressList?.data?.sellerAddresses}
                valueKey={"vendorLocationDisplayName"}
                displayKey={"vendorLocationDisplayName"}
                value={pascalCase(value ?? "")}
                onChange={(e) => {
                  onChange(e);
                  handleChangeAddress(e);
                }}
              />
            )}
          />
          <ErrorMsgV1 msg={errors?.pickUpLocation?.message} />
        </Grid>
        <Grid xs={2.5}>
          <Typography fontWeight={600} mb={1}>
            Seller address
          </Typography>
          <Typography
            sx={{
              maxWidth: 200,
              overflow: "hidden",
            }}
          >
            {getAddress(selectedAddress)}
          </Typography>
        </Grid>
      </Grid>
      <Box
        display={"flex"}
        sx={{
          justifyContent: "flex-end",
          gap: 4,
          alignItems: "center",
        }}
      >
        <ButtonV1 title={"Cancel"} variant="outlined" onClick={onCloseModal} />
        <ButtonV1 title={"Save"} type="submit" />
      </Box>
    </Box>
  );
};

FGPickUpDetailsModal.propTypes = {
  onCloseModal: PropTypes.func,
  onSavePickUpDetails: PropTypes.func,
  order: PropTypes.object,
};

export default memo(FGPickUpDetailsModal);
